import { Dispatch, ReactElement, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { getMountPath, shouldRedirect } from 'utils/generic/route-manipulation';
import { handleRedirectToSportsBook } from 'utils/sportsbook/handleRedirectToSportsBook';
import {
  Competition,
  FavoriteCompetitionInfo
} from 'interfaces/sportsbook-data-levels';
import { EventCollapse } from 'components/EventCollapse';
import { GlobalIcon } from 'components/GlobalIcon';
import { CompetitionNameWithTooltip } from 'components/Shared/CompetitionNameWithTooltip';
import { CompetitionsFavorite } from 'newcomponents/Shared/sportsbook/CompetitionsFavorite';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import {
  batchActions,
  setClickFromFavorites,
  setTournamentSportIds
} from 'store/actions';
import {
  setGamesByCompetitions,
  setSelectedCompetitions
} from 'store/actions/new-sportsbook';
import { getTournamentSportIds } from 'store/selectors/sport-data';

type Props = {
  competitionsCount: number;
  setCollapsedCount: Dispatch<SetStateAction<number>>;
  collapsedCount: number;
  competitions: FavoriteCompetitionInfo[];
};

export const FavoriteCompetition = ({
  competitionsCount,
  setCollapsedCount,
  collapsedCount,
  competitions
}: Props): ReactElement => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { path, params } = useSbRouteMatch();

  const tournamentSportIds = useSelector(getTournamentSportIds);

  const handleClick = (competition: FavoriteCompetitionInfo) => {
    if (params.competition === String(competition.id)) {
      return;
    }

    const redirectPath = `${getMountPath(path)}/${competition.sportAlias}/${
      competition.regionAlias
    }/${competition.id}`;

    dispatch(setClickFromFavorites(true));

    if (!shouldRedirect(SpringConfigs.PAGE_URLS.prematch)) {
      if (tournamentSportIds?.length) {
        dispatch(setTournamentSportIds(null));
      }

      history.push(redirectPath);
    } else {
      handleRedirectToSportsBook(
        'prematch',
        competition.regionAlias,
        competition.id,
        undefined,
        competition.sportAlias
      );
    }

    dispatch(
      batchActions(
        setGamesByCompetitions(null),
        setSelectedCompetitions({
          data: [competition.id],
          key: competition.sportAlias
        })
      )
    );
  };

  return (
    <EventCollapse
      key={`0-competition`}
      title={`${t('sportsbook.competitions')} (${competitionsCount})`}
      defaultActive={true}
      setCollapsedCount={setCollapsedCount}
      collapsedCount={collapsedCount}
      dataTestId="favCompetitionHeader"
      wrapperClassName="favorite__title"
    >
      <>
        {competitions.map(comp => {
          return (
            <CompetitionNameWithTooltip
              dataTestId="favorite-competition"
              competitionUrl={`${getMountPath(path)}/${comp.sportAlias}/${
                comp.regionAlias
              }/${comp.id}`}
              key={`${comp.id}-competition`}
              competitionItem={comp as unknown as Competition}
              clickHandler={() => handleClick(comp)}
              IconBeforeText={
                <>
                  <CompetitionsFavorite
                    competitionInfo={{
                      id: comp.id,
                      name: comp.name,
                      regionAlias: comp.regionAlias,
                      sportAlias: comp.sportAlias
                    }}
                  />
                  <GlobalIcon
                    name={comp.sportAlias}
                    lib="sports"
                    size={EIconSize._16}
                  />
                </>
              }
              IconAfterText={
                <GlobalIcon
                  lib="flags"
                  name={comp.regionAlias}
                  size={EIconSize._18}
                />
              }
            />
          );
        })}
      </>
    </EventCollapse>
  );
};
